import { normaliseRegistration, registrationFromCallsign } from './identity';
import type { FormatParser, ParsedPoint, ParsedTrack } from './types';
import { ParseError } from './types';
import { kmhToKnots, metresToFeet, mphToKnots, parseLooseNumber } from './units';
import type { XmlNode } from './xml';
import {
  childNamed,
  descendantNamed,
  descendantsNamed,
  htmlToText,
  parseXml,
  textContentOf,
  textOf,
} from './xml';

/**
 * KML, in the four layouts a track actually arrives in.
 *
 * `DATA-SOURCES.md` §1 said to parse defensively rather than assume one layout,
 * and the real exports proved the point: **none of the four Flightradar24 KML
 * files from a Silver account contain a `gx:Track`.** What they contain is a
 * `Route` folder of one `Placemark` per fix — position in the `Point`,
 * timestamp in a `TimeStamp`, and altitude, speed and heading only inside an
 * HTML `<description>` — followed by a `Trail` folder of pairwise `LineString`
 * segments carrying the same values again as ranges.
 *
 * So four readers, tried in this order:
 *
 *  1. `gx:Track` — paired `when` and `gx:coord`. The best case, because it is
 *     the only one where time is a first-class part of the geometry. Google
 *     Earth and several loggers write it, so it stays first.
 *  2. Per-fix `Placemark`s with a `Point` — what FR24 really emits.
 *  3. `Trail` segments — pairwise `LineString`s. Reconstructs the same fixes
 *     from segment endpoints, and is the fallback if a future export drops the
 *     `Route` folder.
 *  4. A plain `LineString` — geometry with no time at all. Imports, and warns
 *     that nothing time-based can be derived from it.
 */

interface FixValues {
  altFt: number;
  gsKt: number;
  trackDeg: number;
  /** The second value where a description carries a range, e.g. `200 ft - 150 ft`. */
  endAltFt: number;
  endGsKt: number;
}

function altitudeToFeet(value: number, unit: string | undefined): number {
  if (!Number.isFinite(value)) {
    return Number.NaN;
  }
  // KML geometry is always metres; feet in a description is FR24's own unit.
  const normalised = (unit ?? 'ft').toLowerCase();
  return normalised === 'm' || normalised.startsWith('met') ? metresToFeet(value) : value;
}

function speedToKnots(value: number, unit: string | undefined): number {
  if (!Number.isFinite(value)) {
    return Number.NaN;
  }
  const normalised = (unit ?? 'kt').toLowerCase();
  if (normalised.startsWith('km')) {
    return kmhToKnots(value);
  }
  if (normalised === 'mph') {
    return mphToKnots(value);
  }
  return value;
}

const ALT_PATTERN = /altitude:?\s*(-?[\d,.]+)\s*(ft|feet|m|metres|meters)?(?:\s*-\s*(-?[\d,.]+))?/i;
const SPEED_PATTERN =
  /speed:?\s*(-?[\d,.]+)\s*(kt|kts|knots|km\/h|kmh|mph)?(?:\s*-\s*(-?[\d,.]+))?/i;
const HEADING_PATTERN = /(?:heading|track|course):?\s*(-?[\d,.]+)/i;

/**
 * Read altitude, speed and heading out of a `<description>`.
 *
 * This is the only place FR24 puts them, so a change to that markup is the most
 * likely way this parser breaks. It fails to `NaN` per field rather than
 * throwing: a track with positions and times is still worth having.
 */
function valuesFromDescription(description: string): FixValues {
  const text = htmlToText(description);
  const alt = ALT_PATTERN.exec(text);
  const speed = SPEED_PATTERN.exec(text);
  const heading = HEADING_PATTERN.exec(text);

  return {
    altFt: altitudeToFeet(parseLooseNumber(alt?.[1]), alt?.[2]),
    endAltFt: altitudeToFeet(parseLooseNumber(alt?.[3]), alt?.[2]),
    gsKt: speedToKnots(parseLooseNumber(speed?.[1]), speed?.[2]),
    endGsKt: speedToKnots(parseLooseNumber(speed?.[3]), speed?.[2]),
    trackDeg: parseLooseNumber(heading?.[1]),
  };
}

/** `lon,lat[,altMetres]` triples, whitespace-separated. */
function parseCoordinates(raw: string): { lat: number; lon: number; altFt: number }[] {
  const result: { lat: number; lon: number; altFt: number }[] = [];
  for (const triple of raw.trim().split(/\s+/)) {
    if (triple === '') {
      continue;
    }
    const parts = triple.split(',');
    if (parts.length < 2) {
      continue;
    }
    const alt = parts.length > 2 ? parseLooseNumber(parts[2]) : Number.NaN;
    result.push({
      lon: parseLooseNumber(parts[0]),
      lat: parseLooseNumber(parts[1]),
      altFt: Number.isFinite(alt) ? metresToFeet(alt) : Number.NaN,
    });
  }
  return result;
}

/** FR24 writes `2026-09-15T21:28:14+00:00`; loggers write `Z`. Both parse. */
function parseWhen(raw: string): number {
  if (raw === '') {
    return Number.NaN;
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

/** Timestamps inside a Trail description: `2026-09-15 21:28:14 UTC` twice. */
function timestampsFromDescription(description: string): number[] {
  const text = htmlToText(description);
  const matches = text.matchAll(/(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\s*UTC)?/g);
  return [...matches].map(([, date, time]) => parseWhen(`${date ?? ''}T${time ?? ''}Z`));
}

/** Layout 1: `gx:Track`, the only layout where time is part of the geometry. */
function readGxTrack(doc: XmlNode): { points: ParsedPoint[]; note: string } | undefined {
  const track = descendantNamed(doc, 'Track');
  if (track === undefined) {
    return undefined;
  }
  const whens = descendantsNamed(track, 'when').map((el) => parseWhen(textOf(el)));
  const coords = descendantsNamed(track, 'coord').map((el) => {
    const parts = textOf(el).split(/[\s,]+/);
    const alt = parseLooseNumber(parts[2]);
    return {
      lon: parseLooseNumber(parts[0]),
      lat: parseLooseNumber(parts[1]),
      altFt: Number.isFinite(alt) ? metresToFeet(alt) : Number.NaN,
    };
  });
  if (coords.length === 0) {
    return undefined;
  }

  // Some writers carry speed and heading in parallel SimpleArrayData columns.
  const arrays = new Map<string, number[]>();
  for (const array of descendantsNamed(track, 'SimpleArrayData')) {
    const name = (array.attributes.name ?? '').toLowerCase();
    arrays.set(
      name,
      descendantsNamed(array, 'value').map((el) => parseLooseNumber(textOf(el))),
    );
  }
  const speeds = arrays.get('speed') ?? arrays.get('groundspeed') ?? [];
  const headings = arrays.get('heading') ?? arrays.get('course') ?? arrays.get('bearing') ?? [];

  const points = coords.map((coord, i) => ({
    tMs: whens[i] ?? Number.NaN,
    lat: coord.lat,
    lon: coord.lon,
    altFt: coord.altFt,
    gsKt: speeds[i] ?? Number.NaN,
    trackDeg: headings[i] ?? Number.NaN,
  }));

  return { points, note: `gx:Track, ${String(points.length)} fixes` };
}

/** Layout 2: one `Placemark` per fix — what Flightradar24 actually exports. */
function readPointPlacemarks(doc: XmlNode): { points: ParsedPoint[]; note: string } | undefined {
  const points: ParsedPoint[] = [];

  for (const placemark of descendantsNamed(doc, 'Placemark')) {
    const point = childNamed(placemark, 'Point');
    if (point === undefined) {
      continue;
    }
    const coords = parseCoordinates(textOf(childNamed(point, 'coordinates')));
    const first = coords[0];
    if (first === undefined) {
      continue;
    }

    const values = valuesFromDescription(textContentOf(childNamed(placemark, 'description')));
    const when = parseWhen(textOf(descendantNamed(placemark, 'when')));
    // A per-fix Placemark's name is its own timestamp; fall back to it so a
    // file that drops the TimeStamp element still yields a timed track.
    const tMs = Number.isFinite(when)
      ? when
      : (timestampsFromDescription(textOf(childNamed(placemark, 'name')))[0] ?? Number.NaN);

    // `IconStyle/heading` is the same number as the description's, as an
    // attribute rather than prose. Prefer it, because it cannot be reworded.
    const iconHeading = parseLooseNumber(textOf(descendantNamed(placemark, 'heading')));

    points.push({
      tMs,
      lat: first.lat,
      lon: first.lon,
      /*
       * The description's feet, not the coordinate's metres. FR24 measures in
       * feet and converts for the coordinate, so the description is the
       * original: 200 ft is written as 60.96 m, and reading the metres back
       * gives 199.99999999999997 ft. Same number, noisier, and it would stop a
       * KML and CSV of one flight hashing alike.
       */
      altFt: Number.isFinite(values.altFt) ? values.altFt : first.altFt,
      gsKt: values.gsKt,
      trackDeg: Number.isFinite(iconHeading) ? iconHeading : values.trackDeg,
    });
  }

  if (points.length === 0) {
    return undefined;
  }
  return { points, note: `Route folder, ${String(points.length)} per-fix placemarks` };
}

/**
 * Layout 3: `Trail` segments. Reconstructs fixes from pairwise LineStrings.
 *
 * Strictly *pairwise*: each Placemark must carry exactly two coordinates, which
 * is what FR24's Trail folder is — one segment per consecutive pair of fixes.
 * Reading any LineString this way would be wrong twice over. A single 500-point
 * polyline would be reduced to its two endpoints and 498 fixes thrown away, and
 * a one-coordinate LineString would come back as two identical points, because
 * the first and last coordinate are the same one.
 */
function readTrailSegments(doc: XmlNode): { points: ParsedPoint[]; note: string } | undefined {
  const points: ParsedPoint[] = [];
  let segments = 0;
  let timedSegments = 0;
  let last: ParsedPoint | undefined;

  for (const placemark of descendantsNamed(doc, 'Placemark')) {
    const lineStrings = descendantsNamed(placemark, 'LineString');
    if (lineStrings.length === 0) {
      continue;
    }
    const coords = lineStrings.flatMap((line) =>
      parseCoordinates(textOf(childNamed(line, 'coordinates'))),
    );
    if (coords.length !== 2) {
      // Not a trail segment. Leave the whole file to `readLineStrings`, which
      // keeps every vertex.
      return undefined;
    }
    const start = coords[0];
    const end = coords[coords.length - 1];
    if (start === undefined || end === undefined) {
      continue;
    }
    segments += 1;

    const description = textContentOf(childNamed(placemark, 'description'));
    const values = valuesFromDescription(description);
    const times = timestampsFromDescription(description);

    if (times.length > 0) {
      timedSegments += 1;
    }

    points.push({
      tMs: times[0] ?? Number.NaN,
      lat: start.lat,
      lon: start.lon,
      altFt: Number.isFinite(values.altFt) ? values.altFt : start.altFt,
      gsKt: values.gsKt,
      trackDeg: values.trackDeg,
    });

    // Held back so only the final segment contributes its far end, which is
    // otherwise the next segment's start point duplicated.
    last = {
      tMs: times[times.length - 1] ?? Number.NaN,
      lat: end.lat,
      lon: end.lon,
      altFt: Number.isFinite(values.endAltFt) ? values.endAltFt : end.altFt,
      gsKt: Number.isFinite(values.endGsKt) ? values.endGsKt : values.gsKt,
      trackDeg: values.trackDeg,
    };
  }

  /*
   * A two-coordinate LineString with no timestamps in its description is not a
   * trail segment, it is a two-point polyline — and the two are the same shape.
   * The timestamps are the discriminator, and they are also the only reason
   * this reader exists: with nothing to recover from the description, taking
   * segment endpoints is strictly worse than `readLineStrings` keeping every
   * vertex.
   */
  if (points.length === 0 || timedSegments === 0) {
    return undefined;
  }
  if (last !== undefined) {
    points.push(last);
  }
  return {
    points,
    note: `Trail folder, ${String(segments)} segment${segments === 1 ? '' : 's'}`,
  };
}

/** Layout 4: bare geometry. No time, so nothing time-based is derivable. */
function readLineStrings(doc: XmlNode): { points: ParsedPoint[]; note: string } | undefined {
  const coords = descendantsNamed(doc, 'coordinates').flatMap((el) => parseCoordinates(textOf(el)));
  if (coords.length === 0) {
    return undefined;
  }
  return {
    points: coords.map((coord) => ({
      tMs: Number.NaN,
      lat: coord.lat,
      lon: coord.lon,
      altFt: coord.altFt,
      gsKt: Number.NaN,
      trackDeg: Number.NaN,
    })),
    note: `Bare coordinates, ${String(coords.length)} positions, no timestamps`,
  };
}

interface KmlIdentity {
  callsign?: string | undefined;
  registration?: string | undefined;
  aircraft?: string | undefined;
}

/**
 * Identity from the `Document` header.
 *
 * FR24 names the document `<callsign>/<registration>`, using `-` for a flight
 * with no callsign — which is every GA flight — and repeats the registration
 * and the type in the description's HTML info card.
 */
function readIdentity(doc: XmlNode): KmlIdentity {
  // A KML without a `Document` puts these on `kml` itself. Falling back to the
  // synthetic document node would look at its only child, `kml`, and find
  // neither.
  const header = descendantNamed(doc, 'Document') ?? descendantNamed(doc, 'kml') ?? doc;
  const name = textOf(childNamed(header, 'name'));
  const description = htmlToText(textContentOf(childNamed(header, 'description')));

  let callsign: string | undefined;
  let registration: string | undefined;

  const parts = name.split('/').map((part) => part.trim());
  if (parts.length === 2) {
    const [rawCallsign, rawRegistration] = parts;
    if (rawCallsign !== undefined && rawCallsign !== '' && rawCallsign !== '-') {
      callsign = rawCallsign;
    }
    if (rawRegistration !== undefined && rawRegistration !== '' && rawRegistration !== '-') {
      registration = normaliseRegistration(rawRegistration);
    }
  } else if (name !== '' && name !== '-') {
    callsign = name;
  }

  // The info card spells the registration with its hyphen, so it wins over the
  // document name's compacted form when both are present.
  const fromCard = /registration\s+([A-Z0-9]{1,3}-?[A-Z0-9]{2,5})\b/i.exec(description);
  if (fromCard?.[1] !== undefined) {
    registration = normaliseRegistration(fromCard[1]);
  }

  // Type is not a `Track` field, but it is the one thing a pilot checks in an
  // import list, and Phase 4's aircraft record will want it.
  const aircraftMatch =
    /aircraft\s*(?:\(([A-Z0-9]{2,4})\))?\s*([A-Za-z0-9 .\-/]{3,40}?)\s+registration/i.exec(
      description,
    );
  const typeCode = aircraftMatch?.[1];
  const typeName = aircraftMatch?.[2]?.trim();
  const aircraft =
    typeName !== undefined && typeName !== ''
      ? `${typeName}${typeCode !== undefined ? ` (${typeCode})` : ''}`
      : undefined;

  return {
    ...(callsign !== undefined ? { callsign } : {}),
    ...(registration !== undefined ? { registration } : {}),
    ...(aircraft !== undefined ? { aircraft } : {}),
  };
}

export function parseKml(text: string): ParsedTrack {
  const doc = parseXml(text);
  if (descendantNamed(doc, 'kml') === undefined && descendantNamed(doc, 'Document') === undefined) {
    throw new ParseError('This XML file is not KML: it has no <kml> or <Document> element.');
  }

  const read =
    readGxTrack(doc) ?? readPointPlacemarks(doc) ?? readTrailSegments(doc) ?? readLineStrings(doc);

  if (read === undefined) {
    throw new ParseError(
      'No track found in this KML. Expected a gx:Track, per-fix Placemarks, or a LineString.',
    );
  }

  const identity = readIdentity(doc);
  const notes = [read.note];
  if (identity.aircraft !== undefined) {
    notes.push(`Aircraft: ${identity.aircraft}`);
  }

  const registration = identity.registration ?? registrationFromCallsign(identity.callsign);

  return {
    source: 'fr24-kml',
    points: read.points,
    // KML says nothing about how the altitude was measured. FR24's is
    // barometric, a logger's is GPS, and this parser cannot tell them apart —
    // so it declines to claim either.
    altitudeRef: 'unknown',
    ...(identity.callsign !== undefined ? { callsign: identity.callsign } : {}),
    ...(registration !== undefined ? { registration } : {}),
    // Nothing this parser can see is worth a warning on its own: a layout it
    // cannot read is a `ParseError`, and everything else about the fixes is
    // normalisation's to judge.
    warnings: [],
    notes,
  };
}

export const fr24KmlParser: FormatParser = {
  format: 'fr24-kml',
  label: 'KML',
  extensions: ['.kml'],
  sniff(text) {
    const head = text.slice(0, 4000);
    return /<kml[\s>]/i.test(head) || /<Document[\s>]/i.test(head);
  },
  parse: (bytes) => parseKml(new TextDecoder().decode(bytes)),
};
